import { Alert, Box } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { Route, RouteProps } from 'react-router-dom';

import { HandleCompanyPreferences } from '~/components/CompanyPreferences';
import { SelectedEmployeeContextRootProvider } from '~/components/SelectedEmployee';
import { HandleUserSettings } from '~/components/UserSettings';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { usePrivateRoute } from './usePrivateRoute';
import { useSxStyles } from './useStyles';

export interface PrivateRouteProps extends RouteProps {
  moduleRestrict?: boolean;
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  moduleRestrict = false,
  component,
  children,
  render,
  ...restProps
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { isAuthenticated, isLoading, hasData } = usePrivateRoute(
    moduleRestrict,
    restProps.path,
  );

  return (
    <Route
      {...restProps}
      render={(props) => {
        if (!isAuthenticated) {
          if (isLoading) {
            return (
              <LoadingView sx={sxStyles.loading}>
                {t('authenticating')}
              </LoadingView>
            );
          } else if (!hasData) {
            return <Box sx={sxStyles.preRoutedContent}></Box>;
          }
          return (
            <Box sx={sxStyles.preRoutedContent}>
              <Alert severity={'error'}>{t('userNotAuthorized')}</Alert>
            </Box>
          );
        }

        return (
          <SelectedEmployeeContextRootProvider>
            <HandleCompanyPreferences />
            <HandleUserSettings />

            {(() => {
              if (children) {
                if (children instanceof Function) {
                  return children(props);
                }

                return children;
              }

              if (component) {
                return React.createElement(
                  component as React.ComponentType<RouteComponentProps>,
                  props,
                );
              }

              return render ? render(props) : null;
            })()}
          </SelectedEmployeeContextRootProvider>
        );
      }}
    />
  );
};
